import React, { useEffect, useMemo, useState } from "react";
import {
  FiFolder,
  FiLink,
  FiPlus,
  FiRefreshCcw,
  FiStar,
} from "react-icons/fi";

import {
  getMenuTree,
  menusCache,
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
  toggleMenuItem,
  reorderMenuItems,
  getMenuFlat,
} from "../../../services/menusServices";

import FiltersBar from "./components/FiltersBar";
import PreviewMega from "./components/PreviewMega";
import EditableTree from "./components/EditableTree";
import MenuItemForm from "./components/MenuItemForm";
import ConfirmDeleteDrawer from "./components/ConfirmDeleteDrawer";

import {
  buildOrderedPayload,
  collectFavorites,
  computeMenuStats,
  filterMenuTree,
  flattenMenuTree,
} from "./utils/menuAdminUtils";

export default function MenusAdmin() {
  const [menuKey, setMenuKey] = useState("header");
  const [locale, setLocale] = useState("fr");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [q, setQ] = useState("");

  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [message, setMessage] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);

      const [treeData, flatData] = await Promise.all([
        getMenuTree({
          menu_key: menuKey,
          locale,
          include_inactive: includeInactive,
        }),
        getMenuFlat({
          menu_key: menuKey,
          locale,
          include_inactive: true,
        }),
      ]);

      setTree(Array.isArray(treeData) ? treeData : []);
      setFlat(Array.isArray(flatData) ? flatData : []);
    } catch (error) {
      console.error("Erreur chargement menus:", error);
      setTree([]);
      setFlat([]);
      setMessage("Impossible de charger les menus.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuKey, locale, includeInactive]);

  const filteredTree = useMemo(() => filterMenuTree(tree, q), [tree, q]);

  const treeFlat = useMemo(() => flattenMenuTree(tree), [tree]);

  const favorites = useMemo(() => {
    if (menuKey !== "header") return [];

    return collectFavorites(tree);
  }, [tree, menuKey]);

  const stats = useMemo(() => computeMenuStats(tree), [tree]);

  const refresh = async () => {
    setMessage("");
    menusCache.clear();
    await fetchData();
  };

  const openCreate = (parentId = null, preset = "folder") => {
    setMessage("");

    const base = {
      parent_id: parentId ?? null,
      menu_key: menuKey,
      locale,
      is_active: true,
      target: "_self",
      rel: "",
      icon: "",
      title_attr: "",
      order_index: 0,
      is_favorite: false,
    };

    if (preset === "folder") {
      setFormInitial({
        ...base,
        label: parentId ? "Nouveau dossier" : "Nouvel onglet",
        link_type: "none",
      });
    }

    if (preset === "page") {
      setFormInitial({
        ...base,
        label: "Nouvelle page",
        link_type: "page",
      });
    }

    if (preset === "url") {
      setFormInitial({
        ...base,
        label: "Nouveau lien",
        link_type: "url",
      });
    }

    if (preset === "favorite") {
      setFormInitial({
        ...base,
        parent_id: null,
        label: "Nouveau raccourci",
        link_type: "page",
        is_favorite: true,
      });
    }

    setFormOpen(true);
  };

  const openEdit = (item) => {
    setMessage("");
    setFormInitial(item);
    setFormOpen(true);
  };

  const onSubmitForm = async (payload) => {
    try {
      setMessage("");

      if (formInitial?.id) {
        await updateMenuItem(formInitial.id, payload);
        setMessage("Élément de menu modifié.");
      } else {
        await createMenuItem(payload);
        setMessage("Élément de menu créé.");
      }

      setFormOpen(false);
      setFormInitial(null);

      await refresh();
    } catch (error) {
      console.error("Erreur sauvegarde menu:", error);
      setMessage("Erreur lors de l’enregistrement de l’élément.");
      throw error;
    }
  };

  const askDelete = (item) => {
    setItemToDelete(item);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete?.id) return;

    try {
      setDeleting(true);
      setMessage("");

      const ok = await deleteMenuItem(itemToDelete.id);

      if (!ok) {
        throw new Error("Suppression impossible.");
      }

      setDeleteOpen(false);
      setItemToDelete(null);

      await refresh();

      setMessage("Élément supprimé.");
    } catch (error) {
      console.error("Erreur suppression menu:", error);
      setMessage(
        "Impossible de supprimer cet élément. Il contient peut-être des sous-éléments."
      );
    } finally {
      setDeleting(false);
    }
  };

  const onToggle = async (id) => {
    try {
      setMessage("");

      await toggleMenuItem(id);
      await refresh();

      setMessage("Statut mis à jour.");
    } catch (error) {
      console.error("Erreur toggle menu:", error);
      setMessage("Impossible de modifier le statut.");
    }
  };

  const onReorder = async (parentId, orderedIds) => {
    try {
      setMessage("");

      const items = buildOrderedPayload(parentId, orderedIds);

      await reorderMenuItems(items);
      await refresh();

      setMessage("Ordre du menu mis à jour.");
    } catch (error) {
      console.error("Erreur reorder menu:", error);
      setMessage("Impossible de réordonner les éléments.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-950 dark:bg-slate-950 dark:text-slate-50 md:p-6">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative p-5 md:p-7">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-600 via-cyan-400 to-emerald-400" />

            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-950/40 dark:text-sky-200">
                  <FiFolder />
                  Organisation des menus
                </div>

                <h1 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
                  Menus du site
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Organisez simplement les onglets, dossiers, sous-dossiers et
                  pages visibles dans les menus du site.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => openCreate(null, "folder")}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700"
                >
                  <FiPlus />
                  Nouvel onglet
                </button>

                <button
                  type="button"
                  onClick={() => openCreate(null, "page")}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-700 transition hover:bg-sky-100 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200"
                >
                  <FiLink />
                  Page en racine
                </button>

                {menuKey === "header" ? (
                  <button
                    type="button"
                    onClick={() => openCreate(null, "favorite")}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-700 transition hover:bg-amber-100 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
                  >
                    <FiStar />
                    Raccourci
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={refresh}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FiRefreshCcw />
                  Rafraîchir
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Éléments" value={stats.total} />
              <StatCard label="Onglets" value={stats.roots} tone="blue" />
              <StatCard label="Dossiers" value={stats.folders} tone="violet" />
              <StatCard label="Pages / liens" value={stats.links} tone="green" />
            </div>
          </div>
        </header>

        <section className="grid gap-3 md:grid-cols-3">
          <GuideCard
            number="1"
            title="Onglet"
            text="Élément principal visible dans la barre du menu."
          />
          <GuideCard
            number="2"
            title="Dossier"
            text="Contient des pages ou d’autres dossiers. Il sert à organiser."
          />
          <GuideCard
            number="3"
            title="Page / lien"
            text="Élément cliquable qui ouvre une page du site ou une URL."
          />
        </section>

        {message ? (
          <div
            className={`rounded-3xl border px-4 py-3 text-sm font-medium ${
              message.includes("Erreur") || message.includes("Impossible")
                ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200"
                : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200"
            }`}
          >
            {message}
          </div>
        ) : null}

        <section className="rounded-[32px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-5">
          <FiltersBar
            menuKey={menuKey}
            onMenuKey={setMenuKey}
            locale={locale}
            onLocale={setLocale}
            includeInactive={includeInactive}
            onIncludeInactive={setIncludeInactive}
            q={q}
            onQ={setQ}
            onSubmit={() => {}}
          />
        </section>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(420px,0.8fr)]">
          <section className="space-y-4">
            <div className="rounded-[32px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-lg font-bold">
                    Structure du menu
                  </h2>

                  <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Un élément à la racine devient un onglet principal. Un
                    élément placé dans un dossier devient un sous-menu.
                  </p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => openCreate(null, "folder")}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-sky-700"
                  >
                    <FiPlus />
                    Ajouter un onglet
                  </button>
                </div>
              </div>

              <div className="mt-5">
                {loading ? (
                  <LoadingTree />
                ) : (
                  <EditableTree
                    tree={filteredTree}
                    onCreateFolder={(parentId) =>
                      openCreate(parentId, "folder")
                    }
                    onCreatePage={(parentId) => openCreate(parentId, "page")}
                    onCreateUrl={(parentId) => openCreate(parentId, "url")}
                    onEdit={openEdit}
                    onDelete={askDelete}
                    onToggle={onToggle}
                    onReorder={onReorder}
                  />
                )}
              </div>
            </div>

            <div className="rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h3 className="font-bold">Conseil simple pour les clients</h3>

              <div className="mt-3 space-y-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                <p>
                  Pour créer un menu clair : commencez par créer des{" "}
                  <strong>onglets principaux</strong>, puis ajoutez des{" "}
                  <strong>dossiers</strong> si vous avez beaucoup de pages.
                </p>

                <p>
                  Exemple : <strong>Vie municipale</strong> →{" "}
                  <strong>Conseil municipal</strong> →{" "}
                  <strong>Les élus</strong>.
                </p>
              </div>
            </div>
          </section>

          <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
            <div className="rounded-[32px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold">Aperçu simplifié</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Visualisation de l’organisation du menu.
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <PreviewMega tree={filteredTree} loading={loading} />
              </div>
            </div>

            {menuKey === "header" ? (
              <div className="rounded-[32px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-5">
                <div className="flex items-center gap-2">
                  <FiStar className="text-amber-500" />
                  <h2 className="text-lg font-bold">Raccourcis du header</h2>
                </div>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Ce sont les éléments marqués comme favoris.
                </p>

                <div className="mt-4 space-y-2">
                  {favorites.length ? (
                    favorites.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => openEdit(item)}
                        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800"
                      >
                        <span className="font-bold">{item.label}</span>
                        <span className="text-xs text-slate-500">
                          Modifier
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      Aucun raccourci pour l’instant.
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      </div>

      <MenuItemForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setFormInitial(null);
        }}
        onSubmit={onSubmitForm}
        initial={formInitial || {}}
        menuKey={menuKey}
        locale={locale}
        allItemsFlat={flat?.length ? flat : treeFlat}
        allItemsTree={tree}
      />

      <ConfirmDeleteDrawer
        open={deleteOpen}
        item={itemToDelete}
        loading={deleting}
        onClose={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function StatCard({ label, value, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200",
    blue:
      "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200",
    violet:
      "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-200",
    green:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200",
  };

  return (
    <div className={`rounded-3xl border p-4 shadow-sm ${tones[tone]}`}>
      <div className="text-xs font-bold uppercase tracking-wide opacity-75">
        {label}
      </div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
    </div>
  );
}

function GuideCard({ number, title, text }) {
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-bold text-white dark:bg-slate-100 dark:text-slate-950">
          {number}
        </div>

        <div>
          <h3 className="font-bold">{title}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function LoadingTree() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="h-16 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}